import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  Tooltip, XAxis, YAxis, CartesianGrid, Legend,
} from "recharts";
import {
  TrendingUp, DollarSign, Users, Globe, MessageSquare, Trophy,
  CalendarClock, FileSignature, Sparkles, Bot, ClipboardList,
  ShieldCheck, ArrowUpRight, RefreshCw, BellRing, CheckCircle2, AlertCircle, Clock,
} from "lucide-react";

const STATUS_COLORS = {
  new:           "#94A3B8",
  contacted:     "#60A5FA",
  website_sent:  "#F59E0B",
  interested:    "#A78BFA",
  closed_won:    "#10B981",
  closed_lost:   "#F87171",
};

const STATUS_LABEL = {
  new: "New", contacted: "Contacted", website_sent: "Website sent",
  interested: "Interested", closed_won: "Closed won", closed_lost: "Closed lost",
};

const money = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

function fmtDay(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return iso; }
}
function fmtMonth(k) {
  try { const [y, m] = k.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "short", year: "2-digit" }); }
  catch { return k; }
}
function timeAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (!then) return iso;
  const sec = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

const KIND_META = {
  note:            { label: "Note",     dot: "#94A3B8" },
  email:           { label: "Email",    dot: "#60A5FA" },
  call:            { label: "Call",     dot: "#F59E0B" },
  sms:             { label: "SMS",      dot: "#A78BFA" },
  chat:            { label: "Chat",     dot: "#10B981" },
  contract_signed: { label: "Contract", dot: "#FF6B47" },
};

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/dashboard/overview")
      .then(({ data }) => { setData(data); setError(""); })
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return <div className="p-10 text-neutral-500 text-sm" data-testid="dash-loading">Loading command center…</div>;
  }
  if (error && !data) {
    return <div className="p-10">
      <div className="pill pill-closed_lost mb-4">{error}</div>
      <button onClick={load} className="lf-btn-outline text-sm" data-testid="dash-retry-btn">Retry</button>
    </div>;
  }

  const k = data.kpis || {};
  const ts = data.time_series || {};
  const dists = data.distributions || {};
  const integrations = data.integrations || {};
  const reminders = data.reminders || {};
  const reactivation = data.reactivation || {};

  return (
    <div className="p-8 max-w-[1400px]" data-testid="dashboard-command-center">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="overline mb-2 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Command center
          </div>
          <h1 className="text-3xl font-bold text-neutral-900">
            {(() => {
              const hr = new Date().getHours();
              const wh = hr < 5 ? "Late night" : hr < 12 ? "Good morning" : hr < 18 ? "Good afternoon" : "Good evening";
              return `${wh} — here's the pulse`;
            })()}
          </h1>
          <p className="text-neutral-600 mt-2 text-sm">
            Every important number for the business — MRR, leads, chats, contracts — in one place.
          </p>
        </div>
        <button onClick={load} className="lf-btn-outline text-sm flex items-center gap-2" data-testid="dash-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
        <KpiTile testId="kpi-mrr"          label="MRR"                value={money(k.mrr)}                    sub="from active subs + add-ons"          icon={DollarSign} accent="emerald" to="/billing" />
        <KpiTile testId="kpi-revenue-mtd"  label="Revenue MTD"        value={money(k.revenue_this_month)}     sub="paid this month"                     icon={TrendingUp} accent="emerald" to="/billing" />
        <KpiTile testId="kpi-active-clients" label="Active clients"   value={k.active_clients}                sub={`${k.total_clients || 0} lifetime`}  icon={Users}      accent="blue"   to="/clients?status=active" />
        <KpiTile testId="kpi-leads-7d"     label="New leads · 7d"     value={k.leads_7d}                      sub={`${k.total_leads || 0} lifetime`}    icon={ClipboardList} accent="amber" to="/leads?since=7d" />
        <KpiTile testId="kpi-widget-chats" label="Widget chats · 7d"  value={k.widget_chats_7d}               sub="inbound conversations"                icon={MessageSquare} accent="violet" to="/widget-chats" />
        <KpiTile testId="kpi-closed-won"   label="Closed won · 30d"   value={k.closed_won_30d}                sub={`${k.closed_won || 0} lifetime`}     icon={Trophy}     accent="amber"  to="/leads?status=closed_won" />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        <KpiTile testId="kpi-bookings"       label="Upcoming bookings"   value={k.bookings_upcoming}     sub="future calendar slots"            icon={CalendarClock} accent="blue" />
        <KpiTile testId="kpi-contracts"      label="Active contracts"    value={k.contracts_active}      sub={`${k.contracts_signed_30d} signed 30d`} icon={FileSignature} accent="coral" />
        <KpiTile testId="kpi-intakes"        label="Intakes pending"     value={k.intakes_pending}       sub="AI Visibility + onboarding"       icon={ClipboardList} accent="amber" />
        <KpiTile testId="kpi-sites"          label="Websites built"      value={k.sites_generated}       sub="lifetime"                          icon={Globe}         accent="blue" />
        <KpiTile testId="kpi-agents"         label="AI agents deployed"  value={k.agents_deployed}       sub="lifetime"                          icon={Bot}           accent="violet" />
        <IntegrationsTile integrations={integrations} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        {/* Leads daily 30d */}
        <div className="lg:col-span-2 bg-white border border-neutral-200 p-5" data-testid="chart-leads-daily">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="overline mb-1">Last 30 days</div>
              <h2 className="text-lg font-bold text-neutral-900">Lead flow</h2>
            </div>
            <div className="text-xs text-neutral-500 font-mono">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 inline-block" style={{background:"#3B82F6"}}/> new</span>
              <span className="inline-flex items-center gap-1 ml-3"><span className="w-2 h-2 inline-block" style={{background:"#10B981"}}/> closed</span>
            </div>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={ts.leads_daily_30d || []} margin={{ top: 8, right: 12, bottom: 4, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#64748B" }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #E2E8F0" }}
                  labelFormatter={fmtDay}
                />
                <Line type="monotone" dataKey="leads"  stroke="#3B82F6" strokeWidth={2} dot={false} name="new leads" />
                <Line type="monotone" dataKey="closed" stroke="#10B981" strokeWidth={2} dot={false} name="closed won" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leads by status donut */}
        <div className="bg-white border border-neutral-200 p-5" data-testid="chart-leads-by-status">
          <div className="overline mb-1">Pipeline</div>
          <h2 className="text-lg font-bold text-neutral-900 mb-3">Leads by status</h2>
          <div style={{ width: "100%", height: 210 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={(dists.leads_by_status || []).filter(s => s.count > 0)}
                  dataKey="count"
                  nameKey="status"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {(dists.leads_by_status || []).map((s) => (
                    <Cell key={s.status} fill={STATUS_COLORS[s.status] || "#94A3B8"} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #E2E8F0" }} formatter={(v, n) => [v, STATUS_LABEL[n] || n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1.5 text-xs">
            {(dists.leads_by_status || []).map((s) => (
              <div key={s.status} className="flex items-center gap-1.5">
                <span className="w-2 h-2 shrink-0" style={{ background: STATUS_COLORS[s.status] }} />
                <span className="text-neutral-600 flex-1">{STATUS_LABEL[s.status] || s.status}</span>
                <span className="font-mono text-neutral-900 font-semibold">{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        {/* Revenue monthly */}
        <div className="lg:col-span-2 bg-white border border-neutral-200 p-5" data-testid="chart-revenue-monthly">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="overline mb-1">Last 12 months</div>
              <h2 className="text-lg font-bold text-neutral-900">Revenue trend</h2>
            </div>
            <Link to="/billing" className="text-xs text-coral hover:underline inline-flex items-center gap-1" data-testid="dash-billing-link">
              Billing <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={ts.revenue_monthly_12m || []} margin={{ top: 8, right: 12, bottom: 4, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 0, border: "1px solid #E2E8F0" }}
                  formatter={(v) => money(v)}
                  labelFormatter={fmtMonth}
                />
                <Bar dataKey="revenue" fill="#FF6B47" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Leads by industry */}
        <div className="bg-white border border-neutral-200 p-5" data-testid="chart-leads-by-industry">
          <div className="overline mb-1">Top verticals</div>
          <h2 className="text-lg font-bold text-neutral-900 mb-3">Leads by industry</h2>
          {(dists.leads_by_industry || []).length === 0 ? (
            <div className="text-sm text-neutral-400 py-8 text-center">No industry data yet.</div>
          ) : (
            <div className="space-y-2">
              {(dists.leads_by_industry || []).map((row, i) => {
                const max = Math.max(...(dists.leads_by_industry || []).map(r => r.count), 1);
                const pct = (row.count / max) * 100;
                return (
                  <div key={row.industry} data-testid={`industry-row-${i}`}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-neutral-700 truncate">{row.industry}</span>
                      <span className="font-mono text-neutral-900 font-semibold">{row.count}</span>
                    </div>
                    <div className="h-2 bg-neutral-100">
                      <div className="h-full" style={{ width: `${pct}%`, background: "#3B82F6" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Add-ons + Recent activity + Reminder deliverability */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-10">
        <ReminderCard reminders={reminders} />
        <ReactivationCard rx={reactivation} />
        <div className="bg-white border border-neutral-200 p-5" data-testid="dash-top-addons">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="overline mb-1">Revenue mix</div>
              <h2 className="text-lg font-bold text-neutral-900">Top add-ons by MRR</h2>
            </div>
            <Link to="/addons" className="text-xs text-coral hover:underline inline-flex items-center gap-1">
              Manage <ArrowUpRight className="w-3 h-3" />
            </Link>
          </div>
          {(dists.top_addons || []).length === 0 ? (
            <div className="text-sm text-neutral-400 py-8 text-center">No active add-ons yet.</div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {(dists.top_addons || []).map((a) => (
                <div key={a.id} className="py-2.5 flex items-center gap-3" data-testid={`addon-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-neutral-900 truncate">{a.name}</div>
                    <div className="text-xs text-neutral-500">{a.clients} active client{a.clients === 1 ? "" : "s"}</div>
                  </div>
                  <div className="font-mono font-bold text-emerald-700 text-sm">{money(a.mrr)}/mo</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-neutral-200 p-5" data-testid="dash-recent-activity">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="overline mb-1">Feed</div>
              <h2 className="text-lg font-bold text-neutral-900">Recent activity</h2>
            </div>
            <span className="text-xs text-neutral-500 font-mono">last {(data.recent_activity || []).length}</span>
          </div>
          {(data.recent_activity || []).length === 0 ? (
            <div className="text-sm text-neutral-400 py-8 text-center">Quiet in here. Get after those leads.</div>
          ) : (
            <div className="divide-y divide-neutral-100">
              {(data.recent_activity || []).map((it, i) => {
                const meta = KIND_META[it.kind] || { label: it.kind, dot: "#94A3B8" };
                const key = it.id ? `${it.kind}-${it.id}` : `${it.kind}-${it.at || i}`;
                const Row = (
                  <div className="py-2.5 flex items-start gap-3" data-testid={`activity-${i}`}>
                    <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: meta.dot }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-neutral-900 truncate">{it.title}</div>
                      {it.subtitle && <div className="text-xs text-neutral-500 truncate">{it.subtitle}</div>}
                    </div>
                    <div className="text-xs text-neutral-400 font-mono shrink-0">{timeAgo(it.at)}</div>
                  </div>
                );
                return it.link
                  ? <Link key={key} to={it.link} className="block hover:bg-neutral-50 -mx-2 px-2 transition">{Row}</Link>
                  : <div key={key}>{Row}</div>;
              })}
            </div>
          )}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <QuickAction to="/discover"        eyebrow="Grow" title="Find new leads →"      copy="Search local businesses without a website in any city." testId="qa-discover" />
        <QuickAction to="/ai-visibility-hub" eyebrow="Retention" title="Command hub →"   copy="Run the AI Visibility audit business from one page."      testId="qa-hub" />
        <QuickAction to="/contracts"       eyebrow="Ops" title="Manage contracts →"     copy="Client agreements, milestones, and payouts."             testId="qa-contracts" />
      </div>
    </div>
  );
}

/* ---------- primitives ---------- */

function KpiTile({ label, value, sub, icon: Icon, accent, testId, to }) {
  const accents = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    blue:    "border-blue-200 bg-blue-50/40",
    amber:   "border-amber-200 bg-amber-50/40",
    violet:  "border-violet-200 bg-violet-50/40",
    coral:   "border-orange-200 bg-orange-50/40",
  };
  const cls = `border ${accents[accent] || "border-neutral-200 bg-white"} p-4 ${to ? "cursor-pointer hover:shadow-md hover:-translate-y-0.5 hover:border-neutral-400 transition group" : ""}`;
  const inner = (
    <>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-neutral-500">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        {to && <ArrowUpRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 text-neutral-500 transition" />}
      </div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{value ?? "—"}</div>
      <div className="text-xs text-neutral-500 mt-0.5 truncate">{sub}</div>
    </>
  );
  return to
    ? <Link to={to} data-testid={testId} className={cls}>{inner}</Link>
    : <div className={cls} data-testid={testId}>{inner}</div>;
}

function IntegrationsTile({ integrations }) {
  const items = [
    { key: "resend",       label: "Resend" },
    { key: "vapi",         label: "Vapi" },
    { key: "twilio",       label: "Twilio" },
    { key: "stripe",       label: "Stripe" },
    { key: "emergent_llm", label: "LLM" },
    { key: "google_places",label: "GPlaces" },
  ];
  const ok = items.filter(i => integrations[i.key]).length;
  return (
    <Link to="/ai-visibility-hub" className="border border-neutral-200 bg-white p-4 hover:shadow-md hover:-translate-y-0.5 hover:border-neutral-400 transition group block" data-testid="kpi-integrations">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-neutral-500">
        <ShieldCheck className="w-3.5 h-3.5" /> Integrations
        <ArrowUpRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 text-neutral-500 transition" />
      </div>
      <div className="text-2xl font-bold text-neutral-900 mt-1.5">{ok}<span className="text-neutral-400 text-base font-normal"> / {items.length}</span></div>
      <div className="grid grid-cols-3 gap-1 mt-2">
        {items.map(i => (
          <span
            key={i.key}
            data-testid={`intg-${i.key}`}
            title={`${i.label}: ${integrations[i.key] ? "configured" : "missing"}`}
            className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 text-center ${integrations[i.key] ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}
          >
            {i.label}
          </span>
        ))}
      </div>
    </Link>
  );
}

function QuickAction({ to, eyebrow, title, copy, testId }) {
  return (
    <Link to={to} data-testid={testId} className="border border-neutral-200 bg-white hover:border-neutral-900 hover:shadow transition p-6 block group">
      <div className="overline mb-2">{eyebrow}</div>
      <div className="text-xl font-bold text-neutral-900 mb-1 group-hover:text-coral transition">{title}</div>
      <p className="text-sm text-neutral-500">{copy}</p>
    </Link>
  );
}

function ReactivationCard({ rx }) {
  const money = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;
  const r = rx || {};
  const tiles = [
    { label: "Pending touches", value: r.pending_touches || 0, accent: "text-amber-700 bg-amber-50 border-amber-200" },
    { label: "Responded · 7d",  value: r.responded_7d || 0,    accent: "text-blue-700 bg-blue-50 border-blue-200" },
    { label: "Booked · 7d",     value: r.booked_7d || 0,       accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  ];
  const digestAgo = r.last_digest_at ? timeAgo(r.last_digest_at) : null;
  return (
    <Link to="/reactivation" className="bg-white border border-neutral-200 p-5 block hover:shadow-md hover:border-neutral-400 transition group" data-testid="dash-reactivation">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="overline mb-1">Reactivation</div>
          <h2 className="text-lg font-bold text-neutral-900 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-coral" /> Campaign flow
          </h2>
        </div>
        <ArrowUpRight className="w-4 h-4 text-neutral-400 group-hover:text-coral transition" />
      </div>
      <div className="grid grid-cols-3 gap-2 mb-3">
        {tiles.map((t) => (
          <div key={t.label} className={`border ${t.accent} p-2.5`}>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{t.label}</div>
            <div className="text-xl font-bold mt-0.5">{t.value}</div>
          </div>
        ))}
      </div>
      <div className="space-y-1.5 text-xs">
        <div className="flex justify-between"><span className="text-neutral-500">Revenue 7d</span><span className="font-mono font-bold text-emerald-700">{money(r.revenue_7d)}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Revenue in-flight</span><span className="font-mono font-bold text-emerald-700">{money(r.revenue_in_flight)}</span></div>
        <div className="flex justify-between"><span className="text-neutral-500">Active campaigns · lifetime leads</span><span className="font-mono">{r.active_campaigns || 0} · {r.leads_total || 0}</span></div>
        {digestAgo && (
          <div className="flex justify-between pt-1.5 border-t border-neutral-100" data-testid="dash-reactivation-digest">
            <span className="text-neutral-500">Last digest sent</span>
            <span className="font-mono text-neutral-700">{digestAgo} · {r.digests_sent_7d || 0} this wk</span>
          </div>
        )}
        {r.last_digest_error && (
          <div className="pt-1.5 border-t border-rose-100 text-rose-700 text-[11px] leading-snug" data-testid="dash-reactivation-digest-error" title={r.last_digest_error}>
            <span className="font-mono uppercase tracking-widest text-[9px]">Digest error</span>
            <div className="truncate">{r.last_digest_error}</div>
          </div>
        )}
        {(r.needs_human || 0) > 0 && (
          <div className="flex justify-between pt-1.5 border-t border-neutral-100 text-amber-800 font-semibold" data-testid="dash-reactivation-needs-human">
            <span>Needs human follow-up</span><span className="font-mono">{r.needs_human}</span>
          </div>
        )}
      </div>
    </Link>
  );
}

function ReminderCard({ reminders }) {
  const r = reminders || {};
  const rows = [
    { label: "Pending",        value: r.pending || 0,       icon: Clock,         accent: "text-amber-700 bg-amber-50 border-amber-200" },
    { label: "Sent (lifetime)", value: r.sent_total || 0,    icon: CheckCircle2,  accent: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { label: "Failed (lifetime)", value: r.failed_total || 0, icon: AlertCircle,   accent: "text-rose-700 bg-rose-50 border-rose-200" },
  ];
  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid="dash-reminders">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="overline mb-1">Deliverability</div>
          <h2 className="text-lg font-bold text-neutral-900 inline-flex items-center gap-2">
            <BellRing className="w-4 h-4 text-coral" /> Booking reminders
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {rows.map((row) => (
          <div key={row.label} className={`border ${row.accent} p-2.5`} data-testid={`reminder-${row.label.split(" ")[0].toLowerCase()}`}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest opacity-70">
              <row.icon className="w-3 h-3" /> {row.label}
            </div>
            <div className="text-xl font-bold mt-0.5">{row.value}</div>
          </div>
        ))}
      </div>

      <div className="space-y-2 text-xs">
        {r.next_due?.at && (
          <div className="flex items-start gap-2 pt-1" data-testid="reminder-next-due">
            <Clock className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-neutral-500 font-mono uppercase tracking-widest text-[9px]">Next due</div>
              <div className="text-neutral-800 truncate">
                {r.next_due.name || "Someone"} · {r.next_due.service || "appt"}
                <span className="text-neutral-400 ml-1">· {formatShort(r.next_due.at)}</span>
              </div>
            </div>
          </div>
        )}
        {r.last_sent?.at && (
          <div className="flex items-start gap-2" data-testid="reminder-last-sent">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-neutral-500 font-mono uppercase tracking-widest text-[9px]">Last sent</div>
              <div className="text-neutral-800 truncate">
                {r.last_sent.name || "Someone"} · {r.last_sent.service || "appt"}
                <span className="text-neutral-400 ml-1">· {timeAgo(r.last_sent.at)}</span>
              </div>
            </div>
          </div>
        )}
        {r.last_failed?.at && (
          <div className="flex items-start gap-2" data-testid="reminder-last-failed">
            <AlertCircle className="w-3.5 h-3.5 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-neutral-500 font-mono uppercase tracking-widest text-[9px]">Last failed</div>
              <div className="text-neutral-800 truncate" title={r.last_failed.error || ""}>
                {r.last_failed.name || "Someone"}
                <span className="text-rose-600 ml-1">· {r.last_failed.error || "unknown error"}</span>
              </div>
            </div>
          </div>
        )}
        {!r.next_due?.at && !r.last_sent?.at && !r.last_failed?.at && (r.pending || 0) === 0 && (r.sent_total || 0) === 0 && (
          <div className="text-neutral-400 py-2">
            No reminders yet. They'll appear here once the AI books its first appointment.
          </div>
        )}
        {(r.skipped_total || 0) > 0 && (
          <div className="text-neutral-400 text-[11px] pt-1 border-t border-neutral-100 mt-2" data-testid="reminder-skipped">
            {r.skipped_total} skipped · unparseable time, missing phone, or Twilio not configured
          </div>
        )}
      </div>
    </div>
  );
}

function formatShort(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
